/**
 * ConceptGroupIdFallback — bug-6 fallback for the AV Definition tab (OR-6018,
 * WS-C).
 *
 * The artifact carries two INDEPENDENT concept-group carriers: structured
 * ``conceptMappings`` (term → groups) and a flat ``conceptGroupIds`` list. When
 * a binding is mid-draft / placeholder, ``conceptMappings`` is empty but
 * ``conceptGroupIds`` still holds the ids the agent committed to. The
 * Concept-mappings table gates on ``conceptMappings`` and so hides entirely —
 * leaving nothing on screen.
 *
 * This presentational list resolves each id route-independently (via
 * ``ResolvedConceptGroupChip`` → ``useGetConceptGroupDetailsQuery``) and renders
 * a named chip when the id resolves, or the chip's UNAVAILABLE state when it
 * 404s / is a placeholder — so a broken binding is surfaced rather than hidden.
 *
 * Route-independent on purpose: the AV-detail tab mounts off the project route,
 * so the project-pool ``useConceptGroups()`` hook (which keys off route
 * ``projectId``) can't resolve these ids.
 */

import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';

import { ResolvedConceptGroupChip } from './ResolvedConceptGroupChip';
import { useBatchPrewarmConceptGroups } from '../../hooks/useBatchPrewarmConceptGroups';

interface ConceptGroupIdFallbackProps {
  conceptGroupIds: string[];
  /** Optional dataset release context for the concept-group details prevalence panel. */
  datasetReleaseId?: string | null;
  datasetReleaseDisplayName?: string | null;
}

export const ConceptGroupIdFallback = ({
  conceptGroupIds,
  datasetReleaseId = null,
  datasetReleaseDisplayName = null,
}: ConceptGroupIdFallbackProps) => {
  const { formatMessage } = useIntl();
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);

  // OR-6858: batch every id this fallback will resolve into one request.
  const deferConceptGroupFetch = useBatchPrewarmConceptGroups(conceptGroupIds);

  if (conceptGroupIds.length === 0) return null;

  return (
    <Box data-testid="artifact-concept-group-id-fallback">
      <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 2 }}>
        {formatMessage(
          { id: 'epiChat.conceptGroupFallback.heading' },
          { count: conceptGroupIds.length }
        )}
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start">
        {conceptGroupIds.map((id) => (
          <ResolvedConceptGroupChip
            key={id}
            conceptGroupId={id}
            onOpen={setOpenConceptGroupId}
            deferFetch={deferConceptGroupFetch}
            maxWidth="100%"
          />
        ))}
      </Stack>

      {openConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={openConceptGroupId}
          datasetReleaseId={datasetReleaseId}
          datasetReleaseDisplayName={datasetReleaseDisplayName}
          onClose={() => setOpenConceptGroupId(null)}
        />
      )}
    </Box>
  );
};
